// ============================================================================
// NewTeamObjectiveDialog — modal « Nouvel objectif d'equipe » (L10, F-164)
// ============================================================================
// Portage maison du PoC new-team-objective-dialog.tsx : modal locale (tokens DS,
// gabarit NewPostingDialog E1d — pas de dependance base-ui) + hook E1b
// useCreateObjective (meme endpoint /api/teams/:id/objectives, meme garde M-204 —
// aucune source d'ecriture dupliquee pour les objectifs).
// M-156 : statuses VERBATIM pending/in_progress/done ; un objectif nait `pending`
// (parite PoC — le wizard n'expose pas le statut, le cycle se fait a la carte).

import { useState } from 'react';
import { X } from 'lucide-react';
import { GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import { useCreateObjective, teamErrorCode } from '../../hooks/useTeams';

export function NewTeamObjectiveDialog({
  open,
  onClose,
  onError,
  teamId,
}: {
  open: boolean;
  onClose: () => void;
  /** Remonte le code court d'erreur a la page (alerte ?error= — F-155). */
  onError: (code: string) => void;
  teamId: string;
}) {
  const { t } = useTranslation();
  const create = useCreateObjective(teamId);

  const [title, setTitle] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [stepError, setStepError] = useState<string | null>(null);

  if (!open) return null;

  function close() {
    setTitle('');
    setTargetDate('');
    setStepError(null);
    onClose();
  }

  function submit() {
    if (!title.trim()) {
      setStepError(t('gest:monEquipe.objectives.form.titleRequired'));
      return;
    }
    setStepError(null);
    create.mutate(
      // M-156 : statut initial VERBATIM 'pending' (le serveur re-valide l'enum).
      { title: title.trim(), status: 'pending', targetDate: targetDate || null },
      {
        onSuccess: () => close(),
        onError: (err) => {
          close();
          onError(teamErrorCode(err));
        },
      },
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('gest:monEquipe.objectives.form.title')}
    >
      <div className="flex max-h-full w-full max-w-lg flex-col gap-4 overflow-y-auto rounded-xl border border-slate-200 bg-white p-5 shadow-md dark:border-slate-700 dark:bg-slate-800">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-body font-semibold text-slate-900 dark:text-slate-100">
            {t('gest:monEquipe.objectives.form.title')}
          </h2>
          <button
            type="button"
            onClick={close}
            aria-label={t('gest:monEquipe.objectives.form.cancel')}
            className="touch-manipulation rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="new-objective-title" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:monEquipe.objectives.form.objectiveTitle')}
          </label>
          <input
            id="new-objective-title"
            autoFocus
            maxLength={200}
            placeholder={t('gest:monEquipe.objectives.form.objectiveTitlePlaceholder')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={`${GS_COMPONENTS.input}`}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="new-objective-date" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:monEquipe.objectives.form.targetDate')}
          </label>
          <input
            id="new-objective-date"
            type="date"
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
            className={`${GS_COMPONENTS.input} w-48`}
          />
        </div>

        {stepError ? (
          <p role="alert" className="text-field text-red-600 dark:text-red-400">{stepError}</p>
        ) : null}

        <div className="mt-1 flex items-center justify-end gap-2">
          <button type="button" onClick={close} className={`${GS_COMPONENTS.btnSecondary} touch-manipulation`}>
            {t('gest:monEquipe.objectives.form.cancel')}
          </button>
          <button type="button" onClick={submit} disabled={create.isPending} className={GS_COMPONENTS.btnAction}>
            {t('gest:monEquipe.objectives.form.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}
